import { useMemo, useCallback, useRef, useState } from "react";
import { useQuery } from "@apollo/client/react";
import {
  ActivityViewDataDocument,
  type ActivityViewDataQuery,
  IssueNotesDataDocument,
} from "#app/lib/data";
import type { ActivityNode, NoteNode } from "#app/components/issue-view/activity/activity-types";
import { usePagedConnection } from "#app/lib/hooks/use-paged-connection";
import { useRegisterSubscriptions } from "#app/lib/subscriptions/subscription-provider";
import { activitySubscriptions } from "./activity-subscriptions";

const PAGE_SIZE = 50;

/**
 * Fetch activity-view data via the bundled ActivityViewData query (core)
 * plus IssueNotesData (apiv2) in parallel.
 *
 * Activities paginate through `usePagedConnection` (per-page cache keyed by
 * absolute offset). activityDates rides on the activities response.
 * Notes are loaded once from apiv2; the issue view rarely has hundreds of
 * notes, so a single non-paginated load is fine.
 */
export function useActivityViewData(issueId: string | undefined) {
  const skip = !issueId;

  // Feed realtime: activityChanged upserts/deletes for this issue, handled
  // by cache writes (see activity-subscriptions.ts). Registered here so any
  // surface rendering the feed gets it; the registry dedupes the wire sub.
  useRegisterSubscriptions(issueId ? activitySubscriptions(issueId) : []);

  const variables = useMemo(
    () => ({
      issueId: issueId!,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
    [issueId],
  );

  // activityDates rides on every activities page response. Track the latest.
  const [activityDates, setActivityDates] = useState<string[]>([]);
  const lastDatesRef = useRef<unknown>(null);

  const onData = useCallback((data: ActivityViewDataQuery | undefined) => {
    if (!data) return;
    const dates = data.activityDates;
    if (dates === lastDatesRef.current) return;
    lastDatesRef.current = dates ?? null;
    setActivityDates((dates ?? []) as string[]);
  }, []);

  const {
    items: activities,
    total: activityTotal,
    hasMore: hasMoreActivities,
    loading: activitiesLoading,
    loadingMore,
    fetchPage,
    refetch: refetchActivities,
  } = usePagedConnection<ActivityNode>({
    document: ActivityViewDataDocument,
    variables,
    pageSize: PAGE_SIZE,
    skipQuery: skip,
    getConnection: (d) => d?.activities,
    onData,
  });

  // Notes routed to apiv2. Fires in parallel with the activities query;
  // both share the issueId variable so they refetch together on issue
  // navigation.
  const notesVars = useMemo(() => ({ issueId: issueId! }), [issueId]);
  const {
    data: notesData,
    loading: notesLoading,
    refetch: refetchNotes,
  } = useQuery(IssueNotesDataDocument, {
    variables: notesVars,
    skip,
    fetchPolicy: "cache-and-network",
  });

  const { notes, notesMap } = useMemo(() => {
    const edges = notesData?.notes?.edges ?? [];
    const arr = edges.map((e) => ({
      ...e.node,
      replies: e.node.replies?.edges?.map((re) => re.node) ?? [],
    })) as NoteNode[];
    const map = new Map<string, NoteNode>();
    for (const n of arr) {
      map.set(n.noteId, n);
      n.replies?.forEach((r) => map.set(r.noteId, r));
    }
    return { notes: arr, notesMap: map };
  }, [notesData]);

  const getNote = useCallback(
    (noteId: string): NoteNode | undefined => notesMap.get(noteId),
    [notesMap],
  );

  const loadMoreActivities = useCallback(async () => {
    if (!hasMoreActivities) return false;
    if (activities.length === 0) return false;
    await fetchPage(activities.length, PAGE_SIZE);
    return true;
  }, [hasMoreActivities, activities.length, fetchPage]);

  const refetch = useCallback(async () => {
    await Promise.all([refetchActivities(), refetchNotes()]);
  }, [refetchActivities, refetchNotes]);

  return {
    activities,
    activityTotal,
    activityDates,
    notes,
    notesMap,
    getNote,
    loadMoreActivities,
    hasMoreActivities,
    loadingMore,
    loading: activitiesLoading || notesLoading,
    error: undefined,
    refetch,
  };
}
